import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

/**
 * Analytics → Costs (COGS per SKU, packaging, processing fees, ShipStation label costs) and the
 * ad spend connections (Settings → Customer events → Ad spend). Credentials never come back: last 4s only.
 */
export type CostPrice = { id: string; landed_cost: number | null; packaging_cost: number | null; effective_from: string; note: string | null; created_at: string }
export type SkuCost = { sku: string; product: string | null; variant: string | null; product_cost_per_item: number | null; units_90d: number; in_catalog: boolean; price: number | null; last_sold_at: string | null; current: CostPrice | null; upcoming: CostPrice[]; history: CostPrice[]; missing: boolean }
export type FeeSetting = {
  percent: number
  fixed: number
  refunds_return_fee: boolean
  effective_from: string
  interchange_percent?: number
  card_brand_percent?: number
  markup_percent?: number
  per_sale_fixed?: number
  decline_per_order?: number
  decline_fee?: number
  refund_fee?: number
  void_fee?: number
  chargeback_fee?: number
  chargeback_fee_refunded_on_win?: boolean
  verification_fee?: number
  count_check_timeouts?: boolean
  monthly_fixed?: number
  note?: string | null
  saved_at?: string
}
export type LabelStatus = { connected: boolean; shipped_120d: number; missing_120d: number; orders_with_cost: number; last_label_at: string | null }
export type CostsResponse = { today: string; skus: SkuCost[]; missing: number; missing_in_catalog?: number; order_packaging: { current: CostPrice | null; history: CostPrice[] }; fees: FeeSetting[]; labels: LabelStatus }

const COSTS = ["profit", "costs"]
const P = "/admin/terify/profit"

export const useCosts = () => useQuery({ queryKey: COSTS, queryFn: () => sdk.client.fetch<CostsResponse>(`${P}/costs`) })

function useCostMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => (qc.invalidateQueries({ queryKey: COSTS }), qc.invalidateQueries({ queryKey: ["analytics"] })) })
}
export const useSaveSkuCosts = () => useCostMutation((rows: { sku: string; landed_cost: number | null; packaging_cost?: number | null; effective_from: string; note?: string }[]) => sdk.client.fetch<{ orders_repriced: number }>(`${P}/costs`, { method: "POST", body: { rows } }))
export const useDeleteCost = () => useCostMutation((id: string) => sdk.client.fetch(`${P}/costs/${id}`, { method: "DELETE" }))
export const useSaveOrderPackaging = () => useCostMutation((body: { packaging_cost: number; effective_from: string }) => sdk.client.fetch(`${P}/costs/order-packaging`, { method: "POST", body }))
export const useSaveFees = () => useCostMutation((body: FeeSetting) => sdk.client.fetch(`${P}/fees`, { method: "POST", body }))

// ── Processing fees: profiles, cost model parts, monthly true-ups, chargebacks ──
export type FeeParts = {
  profile?: string
  interchange_percent: number
  card_brand_percent: number
  markup_percent: number
  per_sale_fixed: number
  decline_per_order: number
  decline_fee: number
  refund_fee: number
  refunds_return_fee: boolean
  void_fee: number
  chargeback_fee: number
  chargeback_fee_refunded_on_win: boolean
  verification_fee: number
  count_check_timeouts: boolean
  monthly_fixed: number
  effective_from: string
  note?: string
}
export type FeeProfile = { id: string; name: string; default: boolean; accounts: string[]; history: FeeSetting[]; current: FeeSetting | null; chargeback_limit_percent?: number | null; draft?: boolean; note?: string | null }
/** total_cost = the actual total used: processor_total + gateway_total once both are in (or a single total saved before). */
export type TrueUp = { profile: string; month: string; interchange_percent: number | null; processor_total?: number | null; gateway_total?: number | null; total_cost: number | null; note?: string | null; saved_at?: string }
export type FeeMonth = {
  month: string
  profile: string
  orders: number
  charged: number
  order_fees: number
  fee_back: number
  refunds: number
  refund_fees: number
  void_fees: number
  voids: number
  decline_costs: number
  declines: number
  chargeback_fees: number
  chargebacks: number
  chargeback_amounts: number
  verification_costs: number
  verifications: number
  fixed: number
  true_up: number
  estimate: number
  true_up_entry: TrueUp | null
  closed: boolean
}
export type FeesOverview = { today: string; fees: FeeSetting[]; profiles: FeeProfile[]; true_ups: TrueUp[]; months: FeeMonth[]; accounts_seen: { orders: { key: string; orders: number }[]; cards: { key: string; cards: number }[] } }
export type Chargeback = { id: string; order_id: string; order_name: string | null; amount: number; fee: number | null; disputed_on: string; status: "open" | "won" | "lost"; reason: string | null; note: string | null; trans_id: string | null; cost: number; respond_by: string | null }
export type ReportedChargeback = { trans_id: string; order_id: string; order_name: string | null; amount: number; status: string; submitted_at: string | null }
type Chargebacks = { chargebacks: Chargeback[]; reported: ReportedChargeback[] }

const FEES = ["profit", "fees"]
const CBS = ["profit", "chargebacks"]
export const useFeesOverview = () => useQuery({ queryKey: FEES, queryFn: () => sdk.client.fetch<FeesOverview>(`${P}/fees`) })
/** `status: "open"` = the disputes still to answer, soonest response deadline first (Home → Needs attention). */
export const useChargebacks = (status?: "open") => useQuery({ queryKey: [...CBS, status ?? "all"], queryFn: () => sdk.client.fetch<Chargebacks>(`${P}/chargebacks`, status ? { query: { status } } : {}) })
/** Every fee change re-prices orders: the costs page, the fees page and every report reload. */
function useFeeMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const k of [COSTS, FEES, CBS, ["analytics"]]) qc.invalidateQueries({ queryKey: k })
    },
  })
}
export const useSaveFeeParts = () => useFeeMutation((body: FeeParts) => sdk.client.fetch<FeesOverview>(`${P}/fees`, { method: "POST", body }))
export const useRemoveFeeSetting = () => useFeeMutation((body: { profile?: string; effective_from: string }) => sdk.client.fetch<FeesOverview>(`${P}/fees/remove`, { method: "POST", body }))
export const useSaveFeeProfile = () => useFeeMutation((body: { id?: string; name: string; accounts: string[]; chargeback_limit_percent?: number | null; draft?: boolean; note?: string | null }) => sdk.client.fetch<FeesOverview & { profile: FeeProfile }>(`${P}/fee-profiles`, { method: "POST", body }))
export const useDeleteFeeProfile = () => useFeeMutation((id: string) => sdk.client.fetch<FeesOverview>(`${P}/fee-profiles/${id}`, { method: "DELETE" }))
export const useSaveTrueUp = () => useFeeMutation((body: { profile: string; month: string; interchange_percent: number | null; processor_total: number | null; gateway_total: number | null; note?: string }) => sdk.client.fetch<FeesOverview & { orders_repriced: number }>(`${P}/true-ups`, { method: "POST", body }))
export const useRecordChargeback = () => useFeeMutation((body: { order: string; amount: number; fee?: number | null; disputed_on: string; status: "open" | "won" | "lost"; reason?: string; note?: string; trans_id?: string; respond_by?: string | null }) => sdk.client.fetch<Chargebacks>(`${P}/chargebacks`, { method: "POST", body }))
export const useUpdateChargeback = () => useFeeMutation(({ id, ...body }: { id: string; status?: "open" | "won" | "lost"; respond_by?: string | null }) => sdk.client.fetch<Chargebacks>(`${P}/chargebacks/${id}`, { method: "POST", body }))
export const useDeleteChargeback = () => useFeeMutation((id: string) => sdk.client.fetch<Chargebacks>(`${P}/chargebacks/${id}`, { method: "DELETE" }))
export const usePullLabelCosts = () => useCostMutation(() => sdk.client.fetch<{ pulled: { checked: number; found: number; skipped?: string } }>(`${P}/shipping-costs`, { method: "POST", body: {} }))
export const useBackfillLabelCosts = () => useCostMutation((days: number) => sdk.client.fetch<{ backfill: { ok: boolean; shipments?: number; orders?: number; message?: string } }>(`${P}/shipping-costs/backfill`, { method: "POST", body: { days } }))

// ── Ad spend connections ─────────────────────────────────────────────────────
export type AdPlatform = {
  connected: boolean
  status: "Connected" | "Not connected" | "Switched off"
  missing: string[]
  last4?: Record<string, string>
  account_id: string | null
  login_customer_id: string | null
  enabled: boolean
  connected_at: string | null
  last_sync_at: string | null
  last_sync_ok: boolean | null
  last_sync_error: string | null
  last_sync_rows: number | null
  backfill: { status: "running" | "done" | "failed"; days: number; from: string; to: string; done_days: number; rows: number; error?: string | null } | null
  /** YYYY-MM-DD: spend before it is ignored (never pulled, stored or reported). */
  spend_from: string | null
  /** What the platform says about the ad account (Meta), when it has been read. */
  account: { name: string | null; currency: string | null; timezone: string | null; status: number | null; status_label: string | null; checked_at: string } | null
  /** Time zone / currency / status that don't match the store. */
  warnings: string[]
  sync_failures: number
  alert_sent_at: string | null
}
type AdPlatforms = { platforms: { meta: AdPlatform; google: AdPlatform }; can_manage: boolean }
const ADS = ["settings", "ad-platforms"]
const S = "/admin/terify/settings/ad-platforms"
const A = "/admin/terify/analytics/ad-spend"
/** Polls every 3 s while a backfill runs. */
export const useAdPlatforms = () =>
  useQuery({ queryKey: ADS, queryFn: () => sdk.client.fetch<AdPlatforms>(S), refetchInterval: (q) => (Object.values(q.state.data?.platforms ?? {}).some((p) => p.backfill?.status === "running") ? 3000 : false) })
function useAdsMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSettled: () => qc.invalidateQueries({ queryKey: ADS }) })
}
export const useSaveAdPlatform = () => useAdsMutation(({ id, body }: { id: "meta" | "google"; body: Record<string, unknown> }) => sdk.client.fetch(`${S}/${id}`, { method: "POST", body }))
export const useSaveSpendFloor = () => useAdsMutation((body: { platform: "meta" | "google"; spend_from: string | null }) => sdk.client.fetch<AdPlatforms & { deleted: number }>(`${S}/floor`, { method: "POST", body }))
export const useRemoveAdPlatform = () => useAdsMutation((id: "meta" | "google") => sdk.client.fetch(`${S}/${id}`, { method: "DELETE" }))
export const useSyncAdSpend = () => useAdsMutation((platform: "meta" | "google") => sdk.client.fetch<{ sync: { ok: boolean; rows?: number; status?: string; message?: string } }>(`${A}/sync`, { method: "POST", body: { platform } }))
export const useBackfillAdSpend = () => useAdsMutation((platform: "meta" | "google") => sdk.client.fetch<{ backfill: { ok: boolean; status: string; message?: string; backfill?: { from: string; to: string; days: number } } }>(`${A}/backfill`, { method: "POST", body: { platform, days: 90 } }))
